import React, { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetTitle,
} from '@/components/ui/sheet';

/**
 * Die Leiste liegt über den Leaflet-Kartenebenen (bis ~1000) und über den
 * shadcn-Overlays (10050) — deshalb der sehr hohe Stapelwert, den schon die
 * Fluent-Fassung gesetzt hat.
 */
const SIDE_PANEL_Z_INDEX = 2000000;

interface SidePanelOverlayProps {
    isOpen: boolean;
    onClose: () => void;
    title?: ReactNode;
    subtitle?: ReactNode;
    children: ReactNode;
    width?: string;
}

export const SidePanelOverlay: React.FC<SidePanelOverlayProps> = ({
    isOpen,
    onClose,
    title,
    subtitle,
    children,
    width = '25vw'
}) => {
    const { t } = useTranslation();

    if (!isOpen) return null;

    return (
        <Sheet
            open={isOpen}
            onOpenChange={next => {
                if (!next) onClose();
            }}
        >
            <SheetContent
                side="right"
                data-testid="chat-side-panel"
                className="flex w-auto flex-col p-0 sm:max-w-none"
                // Breite kommt vom Aufrufer, der Stapelwert hält die Leiste
                // über Karte und Dialogen — beides Laufzeitwerte.
                style={{ width, zIndex: SIDE_PANEL_Z_INDEX }}
                overlayStyle={{ zIndex: SIDE_PANEL_Z_INDEX }}
                hideClose
                // Klick daneben schließt nicht — wie in der Fluent-Fassung.
                onInteractOutside={event => event.preventDefault()}
            >
                <div className="border-b">
                    <div className="box-border flex w-full items-start justify-between px-5 pb-0 pt-5">
                        <div className="flex flex-col">
                            {subtitle && <SheetDescription>{subtitle}</SheetDescription>}
                            <SheetTitle className="text-lg font-bold">
                                {title || t('ui.unnamed') || 'Unbenannt'}
                            </SheetTitle>
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            data-testid="chat-side-panel-close"
                            title={t('ui.close') || 'Close'}
                            aria-label={t('ui.close') || 'Close'}
                            onClick={onClose}
                        >
                            <X className="h-4 w-4" aria-hidden />
                        </Button>
                    </div>
                </div>
                <div className="flex-1 overflow-auto p-4">
                    <div className="flex-1">
                        {children}
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};
